<template>
  <BContainer fluid>
    <BRow class="g-0">
      <BCol
        v-if="!standalone"
        class="d-none d-xl-block sidebar"
      >
        <div class="sidebar-item p-0">
          <BButtonGroup
            pills
            vertical
            class="sidebar-sticky"
          >
            <BButton
              disabled
              variant="sidebar"
              class="text-left"
            >
              <!-- style="padding-top:24px" -->
              Sectors
            </BButton>
            <BButton
              v-for="{ colId: optColId, path, name } in freeApiCategories"
              :key="optColId"
              :to="path"
              :active="optColId === colId"
              variant="sidebar"
              class="text-left"
            >
              <BIconCheck2Circle />
              {{ name }}
            </BButton>
          </BButtonGroup>
        </div>
      </BCol>
      <BCol>
        <BContainer
          class="main-container p-0"
          fluid
        >
          <BRow v-if="modalShown === 'go-to-project-modal'">
            <BCol>
              <img
                class="blur-img"
                src="/project-view-blur.png"
                alt="Provider view"
              >
            </BCol>
          </BRow>
          <BRow v-else-if="modalShown === 'go-to-org-modal'">
            <BCol>
              <img
                class="blur-img"
                src="/org-view-blur.png"
                alt="Sector view"
              >
            </BCol>
          </BRow>
          <BRow v-else-if="modalShown === 'go-to-home-modal'">
            <BCol>
              <img
                class="blur-img"
                src="/home-view-blur.png"
                alt="Home page view"
              >
            </BCol>
          </BRow>
          <template v-else>
            <RankingContainer id="ranking" />
            <CloudDetailViewContainer
              id="details"
              ref="details"
            />
            <RealTimeTable
              v-if="!noLiveData"
              id="realtime"
            />
          </template>
          <ShareModal
            modal-id="share-modal"
            :project-view="true"
          />
          <ShareModal
            modal-id="share-col-modal"
            :project-view="false"
          />
        </BContainer>
      </BCol>
    </BRow>
  </BContainer>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import {
  BIconCheck2Circle,
} from 'bootstrap-vue';
import RankingContainer from '../components/RankingContainer.vue';
import CloudDetailViewContainer from '../components/CloudDetailViewContainer.vue';
import RealTimeTable from '../components/RealTimeTable.vue';
import ShareModal from '../components/ShareModal.vue';

export default {
  name: 'HomePage',
  components: {
    RankingContainer,
    CloudDetailViewContainer,
    BIconCheck2Circle,
    RealTimeTable,
    ShareModal,
  },
  props: {
    collectionId: {
      type: String,
      required: false,
      default: 'open-banking-uk-production',
    },
    noLiveData: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  metaInfo() {
    const { path, name: routeName } = this.$route;
    const { name, description } = this;

    const canonical = routeName.startsWith('collection-domain')
      ? this.$router.resolve({ name: `collection-${this.colId}` }).href
      : path;

    return {
      title: name,
      meta: [
        { vmid: 'description', name: 'description', content: description },
        {
          vmid: 'og:description',
          property: 'og:description',
          content: description,
        },
        {
          vmid: 'twitter:description',
          property: 'twitter:description',
          content: description,
        },
        {
          vmid: 'og:url',
          property: 'og:url',
          content: `${window.location.origin}${path}`,
        },
        {
          vmid: 'og:title',
          property: 'og:title',
          content: `${name} - API.expert`,
        },
        {
          vmid: 'twitter:title',
          property: 'twitter:title',
          content: `${name} - API.expert`,
        },
      ],
      link: [
        {
          rel: 'canonical',
          href: `${window.location.origin}${canonical}`,
        },
      ],
    };
  },
  data() {
    return {
      loading: false,
      modalShown: '',
    };
  },
  computed: {
    ...mapGetters([
      'collections',
      'viewMonth',
      'viewYear',
      'timingData',
      'rankingsList',
      'reallySelectedProjectId',
      'currentCollection',
      'name',
    ]),
    headline() {
      return this.currentCollection
        ? this.currentCollection.headline || ''
        : '';
    },
    description() {
      return this.currentCollection
        ? this.currentCollection.description || ''
        : '';
    },
    selectedDomain() {
      if (this.reallySelectedProjectId && this.timingData[0]) {
        return this.timingData[0].domain;
      }
      return '';
    },
    colId() {
      return this.standalone
        ? this.collectionId
        : this.$route.meta.colId;
    },
    standalone() {
      return !(this.$route && this.$route.path);
    },
    freeApiCategories() {
      return Object.entries(this.collections)
        .filter(([, { hidden = false, price }]) => !hidden && price === 0)
        .map(([colId, { name }]) => ({
          colId,
          path: { name: `collection-${colId}` },
          name,
        }));
    },
    paidApiCategories() {
      return Object.entries(this.collections)
        .filter(([, { hidden = false, price }]) => !hidden && price !== 0)
        .map(([colId, { name }]) => ({
          colId,
          path: { name: `collection-${colId}` },
          name,
        }));
    },
  },
  watch: {
    colId() {
      this.updateData();
    },
    viewYear() {
      this.updateData();
    },
    viewMonth() {
      this.updateData();
    },
    selectedDomain(term) {
      if (this.standalone) {
        return;
      }
      const {
        name, hash, query, params,
      } = this.$route;
      const domain = params.domain || '';
      if (term !== domain) {
        if (term) {
          const newName = name.startsWith('collection-domain-')
            ? name
            : name.replace('collection-', 'collection-domain-');
          this.$router.replace({
            name: newName,
            hash,
            query,
            params: { ...params, domain: term },
          });
        } else {
          const newName = name.startsWith('collection-domain-')
            ? name.replace('-domain', '')
            : name;
          this.$router.replace({
            name: newName,
            hash,
            query,
            params: { ...params, domain: undefined },
          });
        }
      }
    },
  },
  mounted() {
    if (!this.$standalone) {
      this.$root.$on('bv::modal::show', (bvEvent, modalId) => {
        this.modalShown = modalId;
        try {
          this.$gtag.event('view_item', {
            event_category: 'engagement',
            event_label: modalId,
          });
        } catch (ex) {
          // eslint-disable-next-line no-console
          console.log(ex);
        }
      });
      this.$root.$on('bv::modal::hide', () => {
        this.modalShown = '';
      });
    }
    this.$root.$on('scroll-to-details', () => {
      const element = this.$refs.details;
      if (element) {
        const top = element.$el.offsetTop;
        window.scrollTo({ left: 0, top, behavior: 'smooth' });
      }
    });
    this.$root.$on('share_network_open', (network) => {
      try {
        this.$gtag.event('share', {
          event_category: 'engagement',
          event_label: network,
        });
      } catch (ex) {
        // eslint-disable-next-line no-console
        console.log(ex);
      }
    });
  },
  async created() {
    await this.updateData();

    const currRoute = this.$route;
    const currDomain = currRoute && currRoute.params && currRoute.params.domain
      ? currRoute.params.domain
      : '';
    if (this.selectedDomain !== currDomain) {
      const { project_key_str: foundProjectId = null } = this.rankingsList.find(
        ({ domain }) => currDomain === domain,
      ) || {};
      if (foundProjectId) {
        this.selectProject(foundProjectId);
      }
    }
  },
  methods: {
    ...mapActions(['loadData', 'search', 'selectProject', 'changeTimePeriod']),
    updateData() {
      return this.loadData({ colId: this.colId });
    },
  },
};
</script>

<style scoped>
.highlight-box {
  border: 2px solid #38384B;
  border-radius: 6px;
  margin-top: 6px;
}
</style>
